import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useCallbackRefState } from "@excalidraw/excalidraw/hooks/useCallbackRefState";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import { restore, restoreAppState } from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { ManageLibraryDialog } from "./components/ManageLibraryDialog";
import { AnimatedGifLayer } from "./components/AnimatedGifLayer";
import { LibraryHubDialog } from "./components/LibraryHubDialog";

import { fetchLibrary, putLibrary } from "./data/library";
import { mediaUrl, type MediaItemDTO } from "./data/media";
import { uploadFrameRenders } from "./data/frames";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
// super-excalidraw: ExportToExcalidrawPlus removed for the self-hosted build.
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

// super-excalidraw: ExcalidrawPlusPromoBanner removed for the self-hosted build.
import { AppSidebar } from "./components/AppSidebar";
import { AnimationDialog } from "./components/AnimationDialog";
import { CommentsLayer } from "./components/CommentsLayer";
import { PresentationPlayer } from "./components/PresentationPlayer";

import type { CollabAPI } from "./collab/Collab";

// super-excalidraw: floating edit ↔ view toggle for embedded diagrams.
import "./components/EmbedViewToggle.scss";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

// super-excalidraw: apply embed-view + lock body classes BEFORE React mounts
// so the first paint already has the chrome hidden. Without this, useEffect
// fires after Excalidraw's initial render and the bottom toolbar flashes on
// refresh before the class arrives. Only runs in iframe (embed) context —
// the native app page must never inherit a saved view/lock preference.
if (
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  window.self !== window.top
) {
  try {
    const __m = window.location.hash.match(/^#(?:json|room)=([^,&]+)/);
    const __id = __m ? __m[1] : "default";
    const __storedView = window.localStorage.getItem(
      `super-excalidraw-view:${__id}`,
    );
    const __initialView =
      __storedView === "1" ||
      (__storedView !== "0" &&
        new URLSearchParams(window.location.search).get("view") === "1");
    if (__initialView) {
      document.body.classList.add("super-excalidraw-view");
    }
    const __storedLock = window.localStorage.getItem(
      `super-excalidraw-lock:${__id}`,
    );
    const __initialLock =
      __storedLock === "1" ||
      (__storedLock !== "0" &&
        new URLSearchParams(window.location.search).get("lock") === "1");
    if (__initialLock) {
      document.body.classList.add("super-excalidraw-locked");
    }
  } catch {
    // localStorage unavailable — fall back to React-managed state.
  }
}

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

// super-excalidraw: upstream's self-embed guard ("I'm not a pretzel!") was
// firing as a false positive in Outline iframe embeds: after window.location
// .reload() from inside the iframe, document.referrer becomes the iframe's
// OWN previous URL, which is same-origin with the current URL, tripping the
// guard. We trust Outline as the embedder, so disable this safety net.
const isSelfEmbedding = false;

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  // super-excalidraw: embed toggles are scoped to iframe context — on the
  // native app page they would (a) overlap the share/sidebar buttons and
  // (b) accidentally inherit prior embed-mode preferences from localStorage.
  const isEmbedded = isRunningInIframe();

  // super-excalidraw: lock-canvas toggle. When locked, the canvas blocks
  // pointer events (no pan, no zoom, no select/draw) so embedded diagrams
  // stay fixed. Persisted per-diagram in localStorage, independent of view
  // mode (you can lock in edit mode too). Embed-only. Persistence priority
  // mirrors the view toggle below:
  //   1. localStorage (per-diagram) — sticky to last user choice.
  //   2. `?lock=1` query param — Outline embeds set this as the default so
  //      doc readers can't accidentally pan/zoom the diagram.
  //   3. false (unlocked) — fallback.
  const [isLocked, setIsLocked] = useState<boolean>(() => {
    if (!isEmbedded) {
      return false;
    }
    try {
      const m = window.location.hash.match(/^#(?:json|room)=([^,&]+)/);
      const key = `super-excalidraw-lock:${m ? m[1] : "default"}`;
      const stored = window.localStorage.getItem(key);
      if (stored === "1") {
        return true;
      }
      if (stored === "0") {
        return false;
      }
      return new URLSearchParams(window.location.search).get("lock") === "1";
    } catch {
      return false;
    }
  });
  // super-excalidraw: embed view-mode toggle. Persistence priority:
  //   1. localStorage (per-diagram) — sticky to last user choice across refresh.
  //   2. `?view=1` query param — Outline embeds set this as the default intent
  //      on first visit; ignored once the user has flipped the toggle.
  //   3. false (full editor) — fallback.
  // Keyed by diagram id from `#json=ID,KEY` / `#room=ID,KEY` so different
  // diagrams remember their own preference. Embed-only.
  const [isViewMode, setIsViewMode] = useState<boolean>(() => {
    if (!isEmbedded) {
      return false;
    }
    try {
      const m = window.location.hash.match(/^#(?:json|room)=([^,&]+)/);
      const key = `super-excalidraw-view:${m ? m[1] : "default"}`;
      const stored = window.localStorage.getItem(key);
      if (stored === "1") {
        return true;
      }
      if (stored === "0") {
        return false;
      }
      return new URLSearchParams(window.location.search).get("view") === "1";
    } catch {
      return false;
    }
  });
  // super-excalidraw: My Diagrams dialog visibility, hoisted here so the
  // hamburger menu, the welcome screen, and any keyboard shortcut can all
  // open the same single dialog instance.
  const [myDiagramsOpen, setMyDiagramsOpen] = useState(false);
  // super-excalidraw: persistent reference to the diagram id+key that the
  // page is currently editing. Captured SYNCHRONOUSLY via useState's lazy
  // initializer (runs once during the first render, before any useEffect)
  // because Excalidraw's own initializeScene effect strips the URL hash via
  // history.replaceState before our effects fire. A useEffect-based capture
  // would read an empty hash and miss the diagram id, so subsequent auto-
  // saves would POST as new diagrams instead of PUTting to the open one.
  // super-excalidraw: per-tab persistence so that reloading the page during
  // (or after) a collab session — when the URL hash is `#room=...` instead
  // of `#json=ID,KEY` — does NOT lose the source diagram and POST a fresh
  // duplicate on the next save. sessionStorage is per-tab, so two tabs
  // editing different diagrams do not collide.
  const SESSION_DIAGRAM_KEY = "super-excalidraw:current-diagram-ref";
  const readSessionDiagramRef = ():
    | { id: string; key: string }
    | null => {
    try {
      const raw = sessionStorage.getItem(SESSION_DIAGRAM_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.id === "string" && typeof parsed.key === "string") {
        return { id: parsed.id, key: parsed.key };
      }
    } catch {}
    return null;
  };
  const writeSessionDiagramRef = (
    ref: { id: string; key: string } | null,
  ) => {
    try {
      if (ref) {
        sessionStorage.setItem(SESSION_DIAGRAM_KEY, JSON.stringify(ref));
      } else {
        sessionStorage.removeItem(SESSION_DIAGRAM_KEY);
      }
    } catch {}
  };

  const [initialDiagramRef] = useState<
    { id: string; key: string } | null
  >(() => {
    if (typeof window === "undefined") {
      return null;
    }
    // 1) URL hash wins (a fresh share-link or post-collab `#json=` URL).
    const m = window.location.hash.match(/^#json=([^,]+),([^&]+)/);
    if (m) {
      return { id: m[1], key: m[2] };
    }
    // 2) Mid-collab reload by the host: the URL is `#room=...` (Excalidraw
    //    changed it when the room started). Recover the original diagram
    //    id+key we stashed on the previous save so the next save PUTs to
    //    the same record instead of POSTing a duplicate. We trust this
    //    only for the same tab — if the page was opened for the FIRST
    //    time directly to a `#room=` link (e.g. a collaborator joining),
    //    sessionStorage is empty and we correctly return null.
    //
    //    NEVER adopt the stashed ref inside an iframe with no hash at all:
    //    same-origin iframes in one tab SHARE sessionStorage, so a bare
    //    base-URL embed (Outline's "insert new Excalidraw") would inherit a
    //    sibling embed's diagram id and autosave its EMPTY canvas over that
    //    diagram (observed 2026-07-17: doc with a #json embed + a fresh
    //    base-URL embed silently wiped the #json diagram).
    if (
      window.self !== window.top &&
      !/^#(?:json|room)=/.test(window.location.hash)
    ) {
      return null;
    }
    return readSessionDiagramRef();
  });
  const currentDiagramRef = useRef<{ id: string; key: string } | null>(
    initialDiagramRef,
  );

  // super-excalidraw: tracks whether frame chrome is currently hidden, so the
  // onChange frame-visibility logic only writes when the desired state flips
  // (loop-safe). Hidden in view/zen/presentation modes, shown when editing.
  const framesHiddenRef = useRef<boolean>(false);
  // super-excalidraw: one-shot guard for the initial fit-to-content pass on
  // embedded diagrams. The scene arrives asynchronously (initialData promise
  // resolves after the API ref lands), so mount-time centering must wait for
  // the first onChange that actually delivers elements — independent of the
  // edit/view toggle.
  const initialCenterDoneRef = useRef<boolean>(false);
  // Track derived id from collab payload so collaborators (whose
  // `currentDiagramRef` is null) can still see + post comments.
  const [collabDiagramId, setCollabDiagramId] = useState<string | null>(null);

  // super-excalidraw: Manage Library dialog visibility. The Media library
  // lives in a right-sidebar tab now (see AppSidebar) — no dialog state
  // needed; the menu entry just toggles the sidebar.
  const [manageLibraryOpen, setManageLibraryOpen] = useState(false);
  // super-excalidraw: Animate dialog visibility.
  const [animateOpen, setAnimateOpen] = useState(false);
  // super-excalidraw: presentation playback. `null` = inactive; an array of
  // frame ids = the running slide order.
  const [presentationSlides, setPresentationSlides] = useState<string[] | null>(
    null,
  );

  useEffect(() => {
    // Cmd/Ctrl+O — open the dashboard instead of the upstream file picker.
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "o") {
        e.preventDefault();
        e.stopPropagation();
        setMyDiagramsOpen(true);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  // super-excalidraw: debounced library push (no excalidrawAPI ref).
  const libSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLibraryChange = (libraryItems: any) => {
    if (libSaveTimerRef.current) {
      clearTimeout(libSaveTimerRef.current);
    }
    libSaveTimerRef.current = setTimeout(() => {
      const items = (libraryItems || []).map((it: any) => ({
        id: it.id,
        status: it.status,
        name: it.name,
        description: it.description ?? "",
        created: it.created,
        elements: it.elements,
      }));
      putLibrary({ items }).catch((e) =>
        console.warn("super-excalidraw: library push failed", e),
      );
    }, 1500);
  };
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  // super-excalidraw: follow the OS theme by default. Upstream defaults to
  // THEME.LIGHT and self-persists it on first init, so on a dark-mode OS the
  // app loads light and only honors the system preference if the user picks
  // "System" by hand (looks "reversed"). We can't gate on the theme key (it's
  // already written by init), so a one-time flag forces "system" on the first
  // load after deploy; any later user choice is then respected forever.
  useEffect(() => {
    try {
      if (!localStorage.getItem("superexcalidraw-theme-default-applied")) {
        localStorage.setItem("superexcalidraw-theme-default-applied", "1");
        setAppTheme("system");
      }
    } catch {
      // localStorage unavailable — leave theme as-is
    }
  }, [setAppTheme]);

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  // super-excalidraw: default PNG export to 2x (HD) instead of 1x. Upstream
  // picks the device pixel ratio as the default, which is 1x on most servers /
  // shared displays. We apply 2x once (one-time flag), then respect whatever
  // the user sets through the export dialog forever.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      if (!localStorage.getItem("superexcalidraw-export-hd-applied")) {
        localStorage.setItem("superexcalidraw-export-hd-applied", "1");
        const currentScale = excalidrawAPI.getAppState().exportScale;
        if (!currentScale || currentScale < 2) {
          excalidrawAPI.updateScene({ appState: { exportScale: 2 } } as any);
        }
      }
    } catch {
      // localStorage unavailable
    }
  }, [excalidrawAPI]);

  // super-excalidraw: Ctrl/Cmd+Shift+D — "duplicate into next frame" for the
  // Magic-Move presentation morph. Copies the selected element(s) into the next
  // slide frame (presentation order, else top-to-bottom by y) at the same
  // frame-relative position, tagging both with a shared customData.morphId so
  // the presentation player tweens them between slides.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.key.toLowerCase() !== "d") {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const api = excalidrawAPI;
      const els = api.getSceneElements() as any[];
      const app = api.getAppState() as any;
      const selIds = Object.keys(app.selectedElementIds || {}).filter(
        (id) => app.selectedElementIds[id],
      );
      const toast = (message: string) => api.setToast?.({ message });
      if (selIds.length === 0) {
        toast("Select element(s) inside a slide frame first");
        return;
      }
      const byId = new Map(els.map((x) => [x.id, x]));
      const frames = els.filter((x) => x.type === "frame" && !x.isDeleted);
      const order: string[] =
        (app.customData?.presentationOrder as string[] | undefined) ??
        frames.slice().sort((a, b) => a.y - b.y).map((f) => f.id);

      const sel = selIds.map((id) => byId.get(id)).filter(Boolean) as any[];
      const srcFrameId = sel.find((x) => x.frameId)?.frameId;
      if (!srcFrameId) {
        toast("Selected elements aren't inside a frame");
        return;
      }
      const srcFrame = byId.get(srcFrameId);
      const nextId = order[order.indexOf(srcFrameId) + 1];
      const nextFrame = nextId ? byId.get(nextId) : null;
      if (!nextFrame) {
        toast("No next slide — add another frame after this one");
        return;
      }

      const patched = new Map<string, any>();
      const additions: any[] = [];
      for (const el of sel) {
        if (el.type === "frame" || el.frameId !== srcFrameId) {
          continue;
        }
        let morphId: string = el.customData?.morphId;
        if (!morphId) {
          morphId = "mph_" + Math.random().toString(36).slice(2, 12);
          patched.set(el.id, {
            ...el,
            customData: { ...(el.customData || {}), morphId },
          });
        }
        additions.push({
          ...el,
          id: "mph-" + morphId + "-" + Math.random().toString(36).slice(2, 8),
          x: nextFrame.x + (el.x - srcFrame.x),
          y: nextFrame.y + (el.y - srcFrame.y),
          frameId: nextId,
          seed: Math.floor(Math.random() * 2147483647),
          versionNonce: Math.floor(Math.random() * 2147483647),
          version: 1,
          updated: Date.now(),
          boundElements: null,
          containerId: null,
          customData: { ...(el.customData || {}), morphId },
        });
      }
      if (additions.length === 0) {
        toast("Nothing to duplicate (select elements within one frame)");
        return;
      }
      const newElements = els
        .map((x) => patched.get(x.id) ?? x)
        .concat(additions);
      api.updateScene({ elements: newElements } as any);
      api.updateScene({
        appState: {
          ...api.getAppState(),
          selectedElementIds: Object.fromEntries(
            additions.map((a) => [a.id, true]),
          ),
        },
      } as any);
      toast(`Duplicated ${additions.length} into the next slide — they'll morph`);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [excalidrawAPI]);

  // super-excalidraw: persist + apply lock state (body class -> CSS sets
  // pointer-events: none on the .excalidraw host while keeping toggles live).
  useEffect(() => {
    if (!isEmbedded) {
      return;
    }
    try {
      const m = window.location.hash.match(/^#(?:json|room)=([^,&]+)/);
      const key = `super-excalidraw-lock:${m ? m[1] : "default"}`;
      window.localStorage.setItem(key, isLocked ? "1" : "0");
    } catch {
      // ignore
    }
    if (typeof document !== "undefined") {
      document.body.classList.toggle("super-excalidraw-locked", isLocked);
    }
  }, [isLocked]);

  // super-excalidraw: apply view-mode side effects (body class, viewModeEnabled
  // appState, fit-to-content) whenever the toggle flips or the API arrives.
  // Also persist the choice to localStorage so refresh restores last state.
  useEffect(() => {
    if (!isEmbedded) {
      return;
    }
    try {
      const m = window.location.hash.match(/^#(?:json|room)=([^,&]+)/);
      const key = `super-excalidraw-view:${m ? m[1] : "default"}`;
      window.localStorage.setItem(key, isViewMode ? "1" : "0");
    } catch {
      // localStorage may be unavailable (private mode etc.) — ignore.
    }
    if (typeof document !== "undefined") {
      document.body.classList.toggle("super-excalidraw-view", isViewMode);
    }
    if (!excalidrawAPI) {
      return;
    }
    // super-excalidraw: toggle view mode. Frame chrome hiding is handled
    // centrally in onChange (keys off zen OR view mode), so it also covers
    // presentation + user-triggered zen mode, not just the embed view.
    excalidrawAPI.updateScene({
      appState: { viewModeEnabled: isViewMode },
    });
    if (isViewMode) {
      try {
        const els = excalidrawAPI.getSceneElements();
        if (els && els.length > 0) {
          (excalidrawAPI as any).scrollToContent(els, {
            fitToContent: true,
            animate: false,
          });
        }
      } catch (err) {
        console.warn("super-excalidraw: scrollToContent failed", err);
      }
    }
  }, [isViewMode, excalidrawAPI]);

  // super-excalidraw: center + fit the diagram on INITIAL load for embeds,
  // regardless of edit/view mode. The view-toggle effect above runs when the
  // API ref lands, but at that point getSceneElements() is still empty (the
  // scene loads async from the server), so its centering silently no-ops —
  // which is why the diagram only used to center after flipping edit → view.
  // This effect waits for the first scene update that has elements, centers
  // once, then unsubscribes. Restoring the saved scroll/zoom is intentional
  // for the native app page, so this stays embed-only.
  useEffect(() => {
    if (!isEmbedded || !excalidrawAPI || initialCenterDoneRef.current) {
      return;
    }
    const centerIfReady = () => {
      if (initialCenterDoneRef.current) {
        return true;
      }
      try {
        const els = excalidrawAPI.getSceneElements();
        if (els && els.length > 0) {
          initialCenterDoneRef.current = true;
          (excalidrawAPI as any).scrollToContent(els, {
            fitToContent: true,
            animate: false,
          });
          return true;
        }
      } catch (err) {
        console.warn("super-excalidraw: initial scrollToContent failed", err);
      }
      return false;
    };
    if (centerIfReady()) {
      return;
    }
    const unsubscribe = (excalidrawAPI as any).onChange(() => {
      if (centerIfReady()) {
        unsubscribe();
      }
    });
    return () => {
      try {
        unsubscribe();
      } catch {
        // already unsubscribed — ignore.
      }
    };
  }, [isEmbedded, excalidrawAPI]);

  // super-excalidraw: NO continuous polling. The embed picks up changes only
  // on iframe load / page reload. This is intentional — the prior 5s meta
  // poll was hitting the Excalidraw API on every embed every 5s, which adds
  // unnecessary load and (worse) was triggering window.location.reload()
  // which tripped upstream's self-embed guard via the post-reload referrer
  // becoming same-origin with the iframe URL.

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  // super-excalidraw: pull the server-side library on mount and merge it
  // into the local Excalidraw state. Server wins on `id` collisions; local-
  // only items get pushed up so we don't lose anything the user already had.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [lib, localState] = await Promise.all([
          fetchLibrary(),
          LibraryIndexedDBAdapter.load(),
        ]);
        if (cancelled) {
          return;
        }
        const localList = ((localState as any)?.libraryItems ?? []) as any[];
        const serverIds = new Set(lib.items.map((it) => it.id));
        const localOnly = localList.filter((it: any) => !serverIds.has(it.id));
        const merged = [...(lib.items as any[]), ...localOnly];
        excalidrawAPI.updateLibrary({
          libraryItems: merged as any,
          merge: false,
          openLibraryMenu: false,
        });
        if (localOnly.length > 0) {
          putLibrary({ items: merged as any }).catch((e) =>
            console.warn("super-excalidraw: initial library push failed", e),
          );
        }
      } catch (e) {
        console.warn("super-excalidraw: library fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [excalidrawAPI]);

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  // super-excalidraw: room → diagram resolution.
  //
  // Excalidraw's collab channel only syncs scene `elements`, not appState,
  // so we cannot piggy-back the host's diagram id on the canvas state.
  // Instead the host PUTs `{diagramId}` to /api/rooms/<roomId> when a room
  // starts; every collaborator GETs that mapping on load (or when the URL
  // hash flips to `#room=...`) and uses the returned id to scope comments
  // (and any other diagram-level UI) to the same bucket the host uses.
  const parseRoomFromHash = (): string | null => {
    const m = window.location.hash.match(/^#room=([^,]+),/);
    return m ? m[1] : null;
  };

  // (a) On initial mount + when URL hash flips to a room, fetch binding.
  useEffect(() => {
    const tryFetch = async () => {
      const roomId = parseRoomFromHash();
      if (!roomId) {
        return;
      }
      try {
        const r = await fetch(
          `/api/rooms/${encodeURIComponent(roomId)}/`,
        );
        if (!r.ok) {
          return;
        }
        const body = await r.json();
        if (body && body.diagramId) {
          setCollabDiagramId(body.diagramId);
        }
      } catch {
        // best-effort
      }
    };
    tryFetch();
    const onHash = () => tryFetch();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // (b) When the host enters a collab room AND we know the underlying
  //     diagram id, register the binding so collaborators can resolve it.
  useEffect(() => {
    if (!isCollaborating) {
      return;
    }
    const roomId = parseRoomFromHash();
    const diagramId = currentDiagramRef.current?.id;
    if (!roomId || !diagramId) {
      return;
    }
    fetch(`/api/rooms/${encodeURIComponent(roomId)}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ diagramId }),
    }).catch((e) =>
      console.warn("super-excalidraw: room binding PUT failed", e),
    );
  }, [isCollaborating]);

  // super-excalidraw: when collab ends and we have a known ref, restore
  // the share-link `#json=ID,KEY` hash so subsequent reloads recover the
  // diagram (without depending on sessionStorage).
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    if (isCollaborating) {
      return;
    }
    const ref = currentDiagramRef.current;
    if (!ref) {
      return;
    }
    const desired = `#json=${ref.id},${ref.key}`;
    if (
      window.location.hash !== desired &&
      !window.location.hash.startsWith("#room=")
    ) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search + desired,
      );
    }
  }, [excalidrawAPI, isCollaborating]);

  // super-excalidraw: debounced auto-save with strict in-flight gating.
  //
  // The previous version called `setTimeout` then ran `exportToBackend`
  // inside the timer. If the user kept editing while a save was still
  // in flight (large diagrams + slow network → easy), the next 1500ms
  // timer would fire BEFORE the first save resolved — so two saves ran
  // concurrently, each seeing `currentDiagramRef.current === null` (the
  // first hadn't yet written it back), and BOTH took the POST branch in
  // exportToBackend. Result: two records in the dashboard for the same
  // edit session — that's the duplicate-on-create bug.
  //
  // Fix: snapshot the latest scene + appState + files on every onChange,
  // and have the timer call a single `triggerAutosave` function that
  // refuses to start while a save is already running. When the save
  // completes, if more changes arrived during the in-flight window, we
  // re-schedule with the freshest snapshot. At most one POST/PUT to
  // `/api/v2` is in flight per browser tab, ever.
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveInFlightRef = useRef<boolean>(false);
  const autosaveLatestRef = useRef<{
    elements: readonly OrderedExcalidrawElement[];
    appState: AppState;
    files: BinaryFiles;
  } | null>(null);

  const triggerAutosave = async () => {
    autosaveTimerRef.current = null;
    if (autosaveInFlightRef.current) {
      // Another save is already running. Keep the latest snapshot and
      // re-arm a short timer so we run again immediately after it finishes.
      autosaveTimerRef.current = setTimeout(triggerAutosave, 250);
      return;
    }
    const latest = autosaveLatestRef.current;
    if (!latest) {
      return;
    }
    autosaveInFlightRef.current = true;
    try {
      const res = await exportToBackend(
        latest.elements as any,
        latest.appState,
        latest.files,
        // Pass the captured id+key so we PUT to the right record
        // even after Excalidraw stripped the hash on load.
        currentDiagramRef.current,
        );
        if (res.url && res.id && res.key) {
          // Remember the id+key for subsequent saves; restore the hash to
          // the URL so reload + share-link still work; persist for the tab
          // so a mid-collab reload doesn't spawn a duplicate diagram.
          currentDiagramRef.current = { id: res.id, key: res.key };
          writeSessionDiagramRef(currentDiagramRef.current);
          const desiredHash = `#json=${res.id},${res.key}`;
          if (
            window.location.hash !== desiredHash &&
            // never clobber a live collab hash — that breaks the room
            !window.location.hash.startsWith("#room=")
          ) {
            window.history.replaceState(
              null,
              "",
              window.location.pathname + window.location.search + desiredHash,
            );
          }
          // Collaborators learn the diagram id via the /api/rooms/<roomId>
          // binding (registered when collab starts), not via appState — see
          // the room-binding useEffects above.

          // Push fresh frame renderings so the MCP / AI feedback loop has
          // up-to-date PNGs to read. Fire-and-forget; never block autosave
          // on this. Skipped during collab (only the host autosaves anyway,
          // and we don't want every keystroke to repaint frames).
          if (excalidrawAPI && currentDiagramRef.current?.id) {
            void uploadFrameRenders({
              diagramId: currentDiagramRef.current.id,
              excalidrawAPI,
            }).catch((e) =>
              console.warn("super-excalidraw: frame render failed", e),
            );
          }
        }
      } catch (e) {
        console.warn("super-excalidraw autosave failed:", e);
      } finally {
        autosaveInFlightRef.current = false;
        // If new edits arrived while we were saving, schedule another
        // run on the freshest snapshot. The 250ms delay lets a burst of
        // post-save onChanges (e.g. cleanup updates) coalesce.
        const latestNow = autosaveLatestRef.current;
        if (latestNow && latestNow !== latest) {
          autosaveTimerRef.current = setTimeout(triggerAutosave, 250);
        }
      }
  };

  // super-excalidraw: force-flush helper exposed on `window` so the
  // dashboard (and anything else that navigates away) can guarantee the
  // current diagram is persisted before the page reloads. Autosave is
  // normally debounced 1500ms, which is wide enough to lose the last
  // keystroke if the user immediately clicks Open on another diagram.
  // The flush cancels the pending debounce timer and runs the save
  // immediately, then waits for the in-flight save (if any) to drain.
  const flushAutosave = async (): Promise<void> => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (autosaveLatestRef.current) {
      await triggerAutosave();
    }
    // If a re-save was scheduled because edits arrived during the save,
    // wait for that one too so the LAST snapshot lands before we return.
    while (autosaveInFlightRef.current || autosaveTimerRef.current) {
      await new Promise((r) => setTimeout(r, 50));
    }
  };

  useEffect(() => {
    (window as any).__superExcalidrawFlushAutosave = flushAutosave;
    return () => {
      if ((window as any).__superExcalidrawFlushAutosave === flushAutosave) {
        delete (window as any).__superExcalidrawFlushAutosave;
      }
    };
  });

  const scheduleAutosave = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    // During a collab session: collaborators (no currentDiagramRef) skip
    // dashboard autosave so they don't accidentally PUT into a record they
    // don't own. The host (currentDiagramRef set) DOES keep autosaving so
    // the dashboard copy stays in sync as the team draws — useful when the
    // host disconnects mid-session and re-opens, or when collab ends.
    if (collabAPI?.isCollaborating() && !currentDiagramRef.current) {
      return;
    }
    // Read-only surfaces never autosave: a view-mode or locked embed cannot
    // be edited, so any save it fires is at best a redundant PUT on load and
    // at worst a stale-scene clobber of the server copy. Read the body
    // classes (kept in sync by the view/lock effects) instead of state to
    // avoid stale-closure issues inside the onChange pipeline.
    if (
      isEmbedded &&
      typeof document !== "undefined" &&
      (document.body.classList.contains("super-excalidraw-view") ||
        document.body.classList.contains("super-excalidraw-locked"))
    ) {
      return;
    }
    // Don't fire autosave on the very first onChange (empty canvas after load).
    // Once at least one non-deleted element exists we treat it as a real edit.
    const hasContent = elements.some((el) => !el.isDeleted);
    if (!hasContent) {
      return;
    }
    // Always store the LATEST snapshot — when the timer fires, that's what
    // gets saved. This means a long save can finish, see the snapshot has
    // moved, and trigger a re-save without ever creating a parallel POST.
    autosaveLatestRef.current = { elements, appState, files };
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(triggerAutosave, 1500);
  };

  // super-excalidraw: clear-canvas helper used by the menu wrapper. Drops
  // the in-memory ref and the URL hash so the very next stroke creates a
  // brand-new diagram rather than overwriting the one that was open.
  const onClearCanvasNewDiagram = () => {
    currentDiagramRef.current = null;
    writeSessionDiagramRef(null);
    setCollabDiagramId(null);
    if (window.location.hash) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  };

  // super-excalidraw: insert a media-library image onto the canvas. Shared by
  // the right-sidebar Media tab and the unified hub's Media tab. Pulls bytes
  // through /api/media/<id>/file and feeds them in as a normal image element
  // — the same path Excalidraw uses for drag-and-drop.
  const handleInsertMedia = async (item: MediaItemDTO) => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      const r = await fetch(mediaUrl(item));
      if (!r.ok) {
        throw new Error(`fetch media ${r.status}`);
      }
      const blob = await r.blob();
      const dataURL: string = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
      const fileId = item.fileId as any;
      excalidrawAPI.addFiles([
        {
          id: fileId,
          dataURL: dataURL as any,
          mimeType: (item.mimeType || "image/png") as any,
          created: Date.now(),
        },
      ]);
      const w = item.width || 240;
      const h = item.height || 180;
      const appState = excalidrawAPI.getAppState();
      const cx = -appState.scrollX + appState.width / 2 - w / 2;
      const cy = -appState.scrollY + appState.height / 2 - h / 2;
      excalidrawAPI.updateScene({
        elements: [
          ...excalidrawAPI.getSceneElementsIncludingDeleted(),
          {
            type: "image",
            version: 1,
            versionNonce: Math.floor(Math.random() * 2147483647),
            isDeleted: false,
            id: `media-${item.id}-${Date.now()}`,
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0,
            x: cx,
            y: cy,
            strokeColor: "transparent",
            backgroundColor: "transparent",
            width: w,
            height: h,
            seed: Math.floor(Math.random() * 2147483647),
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: Date.now(),
            link: null,
            locked: false,
            status: "saved",
            fileId,
            scale: [1, 1],
          } as any,
        ],
      });
    } catch (e) {
      console.warn("super-excalidraw: media insert failed", e);
    }
  };

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // super-excalidraw: hide frame chrome (borders + name labels) whenever the
    // canvas is in a "clean viewing" state — view mode (incl. the embed view),
    // zen mode, or presentation (which enables both). Restore it when back to
    // normal editing so frames stay manageable. frameRendering is runtime-only
    // appState (never persisted), so we drive it here off the live appState.
    // The ref guard makes this loop-safe: our own updateScene re-enters
    // onChange, but zen/view are unchanged so wantHidden matches the ref → no-op.
    if (excalidrawAPI) {
      const wantHidden = !!(appState.zenModeEnabled || appState.viewModeEnabled);
      if (wantHidden !== framesHiddenRef.current) {
        framesHiddenRef.current = wantHidden;
        excalidrawAPI.updateScene({
          appState: {
            frameRendering: {
              enabled: true,
              clip: true,
              name: !wantHidden,
              outline: !wantHidden,
            },
          },
        });
      }
    }

    scheduleAutosave(elements, appState, files);

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  // super-excalidraw: Excalidraw+ command palette entries removed.

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      {/* super-excalidraw: floating Lock + View toggles, fixed top-right.
          Only rendered in iframe (embed) context — on the native app page
          they would overlap the share / sidebar buttons. */}
      {isCollabDisabled && (
        <>
      <button
        type="button"
        className="super-excalidraw-lock-toggle"
        onClick={() => setIsLocked((v) => !v)}
        title={isLocked ? "Unlock canvas" : "Lock canvas (disable pan & zoom)"}
        aria-label={
          isLocked ? "Unlock canvas" : "Lock canvas (disable pan and zoom)"
        }
        aria-pressed={isLocked}
      >
        {isLocked ? "Unlock" : "Lock"}
      </button>
      <button
        type="button"
        className="super-excalidraw-view-toggle"
        onClick={() => {
          // Entering edit mode auto-unlocks: locked mode hides the toolbar
          // and freezes the canvas, so "Edit" on a locked embed would
          // otherwise land in a dead editor.
          if (isViewMode && isLocked) {
            setIsLocked(false);
          }
          setIsViewMode((v) => !v);
        }}
        title={isViewMode ? "Switch to edit mode" : "Switch to read-only view"}
        aria-label={
          isViewMode ? "Switch to edit mode" : "Switch to read-only view"
        }
        aria-pressed={isViewMode}
      >
        {isViewMode ? "Edit" : "View"}
      </button>
        </>
      )}
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        onLibraryChange={onLibraryChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              // super-excalidraw: no Excalidraw+ tab in the export dialog.
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {/* super-excalidraw: no Excalidraw+ promo banner. */}
              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          onOpenMyDiagrams={() => setMyDiagramsOpen(true)}
          onOpenManageLibrary={() => setManageLibraryOpen(true)}
          onOpenMediaLibrary={() =>
            excalidrawAPI?.toggleSidebar({ name: "default", tab: "media" })
          }
          onOpenAnimate={() => setAnimateOpen(true)}
          // Clear canvas: empty in place. Keeps the diagram's id+key+URL
          // hash so autosave PUTs the empty state to the same record.
          onClearCanvas={() => excalidrawAPI?.resetScene()}
          // New diagram: empty canvas + drop ref + drop URL hash, so the
          // very next stroke spawns a fresh dashboard record.
          onNewDiagram={() => {
            excalidrawAPI?.resetScene();
            onClearCanvasNewDiagram();
          }}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
          onOpenMyDiagrams={() => setMyDiagramsOpen(true)}
        />
        {myDiagramsOpen && (
          <LibraryHubDialog
            excalidrawAPI={excalidrawAPI}
            onInsertMedia={handleInsertMedia}
            onClose={() => setMyDiagramsOpen(false)}
          />
        )}
        {manageLibraryOpen && (
          <ManageLibraryDialog onClose={() => setManageLibraryOpen(false)} />
        )}
        {animateOpen && (
          <AnimationDialog
            diagramId={currentDiagramRef.current?.id}
            excalidrawAPI={excalidrawAPI}
            onClose={() => setAnimateOpen(false)}
          />
        )}
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {/* super-excalidraw: no Excalidraw+ overwrite-confirm action. */}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        {presentationSlides && excalidrawAPI && (
          <PresentationPlayer
            excalidrawAPI={excalidrawAPI}
            slides={presentationSlides}
            onEnd={() => setPresentationSlides(null)}
          />
        )}

        {excalidrawAPI && (
          <AnimatedGifLayer excalidrawAPI={excalidrawAPI} />
        )}

        <CommentsLayer
          excalidrawAPI={excalidrawAPI}
          diagramId={
            currentDiagramRef.current?.id ?? collabDiagramId ?? null
          }
        />

        <AppSidebar
          excalidrawAPI={excalidrawAPI}
          diagramId={
            currentDiagramRef.current?.id ?? collabDiagramId ?? null
          }
          onStartPresentation={(ids) =>
            ids.length > 0 && setPresentationSlides(ids)
          }
          onInsertMedia={handleInsertMedia}
        />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            // super-excalidraw: X / Discord / YouTube / Excalidraw+ command
            // palette links removed for the self-hosted build. GitHub stays
            // above as upstream attribution.
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
